import Spinner from 'parkdepot-shared/components/Spinner'
import React, { FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

// hooks
import useAdyenSession from '../../hooks/useAdyenSession'

// components
import Dropin from './Dropin'

// style components
import { LoadingContainer } from './Dropin.style'

// utils
import { useAuth } from '../../context/AuthContext/AuthContext'
import { CREATE_SAVE_PAYMENT_METHOD } from '../../graphql/mutation/createSavePaymentMethod.mutation'
import { useAuthenticatedMutation } from '../../hooks/useAuth'
import { AdyenResultStatus } from '../../types/adyenResultStatus.enum'
import { PaymentMethod } from '../../types/checkout.interface'
import { getDomainExtension } from '../../utils/getDomainExtension'
import { getLocaleForAdyen } from '../../utils/getLocaleForAdyen.utils'

interface Props {
    onCompleted: (resultCode: AdyenResultStatus) => void
    onError: (error: any) => void
    onPaymentMethodChange: (method: PaymentMethod) => void
}

const SavePaymentDropinContainer: FC<Props> = ({
    // orderId,
    // loading,
    // email,
    onCompleted,
    onError,
    onPaymentMethodChange
}) => {
    const { t } = useTranslation()
    const [session, setSession] = useState<{ [key: string]: string }>({})
    const { i18n } = useTranslation()
    const dropInRef = useRef<null | HTMLDivElement>(null)
    const { user } = useAuth()
    const [dropin, setDropin] = useState<any>()
    const [loading, setLoading] = useState(true)
    const onPaymentCompleted = (resultCode: AdyenResultStatus) => {
        onCompleted(resultCode)
    }

    const onPaymentError = (error: any) => {
        onError(error)
    }

    const [createDropin, error] = useAdyenSession(
        session,
        onPaymentCompleted,
        onPaymentError,
        {
            showStoredPaymentMethods: false
        },
        {
            'de-DE': { confirmPreauthorization: t('profile.paymentSection.confirmPaymentMethod') },
            'de-AT': { confirmPreauthorization: t('profile.paymentSection.confirmPaymentMethod') },
            'en-US': { confirmPreauthorization: t('profile.paymentSection.confirmPaymentMethod') }
        }
    )

    const [createSavePaymentMethodSession] = useAuthenticatedMutation({
        mutation: CREATE_SAVE_PAYMENT_METHOD
    })

    useEffect(() => {
        if (!user) return

        const variables = {
            shopper_email: user.email,
            shopper_locale: getLocaleForAdyen(i18n.language),
            top_level_domain: getDomainExtension()
        }

        const createSession = async () => {
            try {
                const response = await createSavePaymentMethodSession({
                    variables
                })
                if (response && response.data) {
                    setSession({
                        id: response.data.b2c_save_payment_method.session_id,
                        sessionData: response.data.b2c_save_payment_method.session_data
                    })
                }
            } catch (error) {
                // log the error here?
            } finally {
                setLoading(false)
            }
        }

        createSession()
    }, [createSavePaymentMethodSession, i18n.language])

    useEffect(() => {
        const initDropin = async () => {
            if (dropin) {
                setDropin(undefined)
            }
            const dropIn = await createDropin()
            setDropin(dropIn)
        }
        if (session?.id) {
            initDropin()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session])

    // a really hacky way to get the payment method...
    useEffect(() => {
        const handleClick = () => {
            if (dropin.activePaymentMethod) {
                setTimeout(() => {
                    onPaymentMethodChange(dropin.activePaymentMethod.type)
                }, 200)
            }
        }
        if (dropInRef && dropInRef.current) {
            dropInRef.current.addEventListener('click', handleClick)
        }
        return () => {
            dropInRef?.current?.removeEventListener('click', handleClick)
        }
    }, [dropin, onPaymentMethodChange])

    return (
        <>
            {!dropin || loading ? (
                <LoadingContainer>
                    <Spinner size="lg" theme="primary" />
                </LoadingContainer>
            ) : (
                <Dropin dropInRef={dropInRef} dropin={dropin} error={error} showTerms={false} />
            )}
        </>
    )
}

export default SavePaymentDropinContainer
